import React, { useState, useEffect } from "react";
import { Eye, EyeOff } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Logo from "../images/Logo.png";
import CoralReef from "../images/CoralReef.jpg";
import NetworkCanvas from "./NetworkCanvas";
import { resetPassword } from "../service/ResetPasswordService";
import { errorMessage } from "../service/HttpErrors";

const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

export default function ResetPassword() {
    const navigate = useNavigate();
    const [token, setToken] = useState("");
    const [form, setForm] = useState({ email: "", password: "", confirm: "" });
    const [show, setShow] = useState({ password: false, confirm: false });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        const t = new URLSearchParams(window.location.search).get("reset");
        if (t) setToken(t);
    }, []);

    const handleChange = (e) => { setForm({ ...form, [e.target.name]: e.target.value }); setError(""); };
    const toggle = (field) => setShow(s => ({ ...s, [field]: !s[field] }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!PASSWORD_REGEX.test(form.password)) {
            setError("Password must be at least 8 characters with 1 uppercase, 1 lowercase, and 1 number.");
            return;
        }
        if (form.password !== form.confirm) {
            setError("Passwords do not match.");
            return;
        }
        setLoading(true);
        try {
            const res = await resetPassword(form.email, form.password, form.confirm, token);
            if (res === true) {
                setDone(true);
            } else {
                setError("Could not reset password. Please try again.");
            }
        } catch (err) {
            setError(errorMessage(err, "Could not reset password. Please try again."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="auth-layout">
            {/* Left panel */}
            <div className="auth-panel" style={{ backgroundImage: `url(${CoralReef})` }}>
                <div className="auth-panel-overlay" />
                <NetworkCanvas />
                <div className="auth-panel-content">
                    <div className="auth-logo-wrap">
                        <img src={Logo} alt="Logo" className="auth-logo" />
                    </div>
                    <h2 className="auth-panel-title">DeepThought</h2>
                    <p className="auth-panel-sub">Research analytics for the modern scientist.</p>
                </div>
            </div>

            {/* Right form */}
            <div className="auth-form-side">
                <NetworkCanvas dark />
                <div className="auth-form-wrap">
                    {done ? (
                        <>
                            <h1 className="auth-heading">Password reset</h1>
                            <p className="auth-sub">Your password has been updated successfully.</p>
                            <button className="auth-submit-btn" style={{ marginTop: 24 }} onClick={() => navigate("/login")}>
                                Back to Login
                            </button>
                        </>
                    ) : (
                        <>
                            <h1 className="auth-heading">Set new password</h1>
                            <p className="auth-sub">Enter your email and choose a new password.</p>

                            <form onSubmit={handleSubmit}>
                                <div className="auth-fields">
                                    <div className="auth-field">
                                        <label className="upload-label">Email</label>
                                        <input
                                            className="upload-input"
                                            type="email"
                                            name="email"
                                            placeholder="you@example.com"
                                            required
                                            value={form.email}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="auth-field">
                                        <label className="upload-label">New Password</label>
                                        <div className="auth-input-wrap">
                                            <input
                                                className="upload-input"
                                                type={show.password ? "text" : "password"}
                                                name="password"
                                                placeholder="••••••••"
                                                required
                                                value={form.password}
                                                onChange={handleChange}
                                            />
                                            <button type="button" className="auth-eye-btn" onClick={() => toggle("password")} tabIndex={-1}>
                                                {show.password ? <EyeOff size={15} /> : <Eye size={15} />}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="auth-field">
                                        <label className="upload-label">Confirm Password</label>
                                        <div className="auth-input-wrap">
                                            <input
                                                className="upload-input"
                                                type={show.confirm ? "text" : "password"}
                                                name="confirm"
                                                placeholder="••••••••"
                                                required
                                                value={form.confirm}
                                                onChange={handleChange}
                                            />
                                            <button type="button" className="auth-eye-btn" onClick={() => toggle("confirm")} tabIndex={-1}>
                                                {show.confirm ? <EyeOff size={15} /> : <Eye size={15} />}
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {error && <p className="chpw-error">{error}</p>}

                                <button className="auth-submit-btn" type="submit" disabled={loading}>
                                    {loading ? "Resetting..." : "Reset Password"}
                                </button>
                            </form>

                            <p className="auth-switch">
                                <button className="auth-link auth-link-bold" onClick={() => navigate("/login")}>
                                    Back to Login
                                </button>
                            </p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
